import {Injectable} from '@angular/core';
import {Presenter} from "../../../internal/presenter/presenter";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";
import {DataService} from "../../../../services/external-api/data.service";
import {forkJoin, Observable} from "rxjs";
import {
  emptyCamundaActivity, emptyCamundaCompleteButton, emptyCamundaForm, emptyCamundaFormValidator,
  emptyCamundaProcessDefinition,
  emptyCamundaProcessInstance, emptyCamundaProcessProfile,
  emptyCamundaTask, emptyCamundaTaskProfile, emptyCamundaTaskState,
  emptyCamundaVariable, ICamundaActivity,
  ICamundaProcessDefinition,
  ICamundaProcessInstance,
  ICamundaTask, ICamundaTaskState,
  ICamundaVariable
} from "@retail-data-factory/rdf-angular-types-api";
import {map} from "rxjs/operators";
import * as moment from "moment";
import {IServiceResponse} from "../../../../services/external-api/response-interfaces";
import {emptyTranslatedName} from "@retail-data-factory/rdf-angular-translation-service";

@Injectable({
  providedIn: 'root'
})
export class ApiCamundaService {

  constructor(
    private configurationManager: ConfigurationManager,
    private presenter: Presenter,
    private dataService: DataService
  ) {
  }


  getProcessDefinitions(): Observable<Array<ICamundaProcessDefinition>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/process-definition?latest=true`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapProcessDefinitionFromService(item));
      })
    );
  }

  getProcessDefinition(id: string): Observable<ICamundaProcessDefinition> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/process-definition/${id}`, {}).pipe(
      map(result => {
        if (result.report && result.report[0]) {
          return this.mapProcessDefinitionFromService(result.report[0]);
        } else {
          return {...emptyCamundaProcessDefinition};
        }
      })
    );
  }

  /**
   * Получить значение сквозного бизнес ключа процессов
   * @param objectType
   * @param objectId
   */
  getBusinessKey(objectType: string, objectId: string): Observable<IServiceResponse> {
    const param = {
      object_type: objectType,
      object_id: objectId
    }
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/businesskey`, param, {}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }


  /**
   * Запуск процесса по ключу
   * @param processKey - business key типа процесса, запустит последнюю версию процесса
   * @param variables - переменные вида {"variable1":{"value": "variable1_value", "type": "string""}, "variable2":{"value": "variable2_value", "type": "string""}}. Более подробнов документации камунды
   * @param businessKey - бизнес ключ инстанса процесса
   */
  startProcessInstance(processKey: string, variables: object, businessKey: string): Observable<IServiceResponse> {
    const newVariables = JSON.parse(JSON.stringify(variables));
    for (let prop in newVariables) {
      if (newVariables[prop].type === 'json') {
        newVariables[prop].value = JSON.stringify(newVariables[prop].value);
      }
    }
    const body = {
      "variables": newVariables,
      businessKey: businessKey
    };


    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/process-definition/key/${processKey}/start`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  getProcessInstances(search: string = '', searchType: string = 'processInstanceId'): Observable<Array<ICamundaProcessInstance>> {
    const queryParams = `?sortBy=startTime&sortOrder=desc${search && searchType ? `&${searchType}=${search}` : ''}`; //`&variables=${searchType}_like_${search}`
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/process-instance${queryParams}`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapProcessInstanceFromService(item));
      })
    );
  }

  getProcessInstance(processInstanceId): Observable<ICamundaProcessInstance> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/process-instance/${processInstanceId}`, {}).pipe(
      map(result => {
        return result.report.length > 0 ? this.mapProcessInstanceFromService(result.report[0]) : JSON.parse(JSON.stringify(emptyCamundaProcessInstance));
      })
    );
  }

  getProcessInstancesRdf(search: string = '', searchType: string = 'processInstanceId'): Observable<Array<ICamundaProcessInstance>> {
    const queryParams = `?sortBy=startTime&sortOrder=desc${search && searchType ? `&${searchType}=${search}` : ''}`; //`&variables=${searchType}_like_${search}`
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/process-instance${queryParams}`, {mode: 'rdf'}).pipe(
      map(result => {
        return result.report.map(item => this.mapProcessInstanceFromService(item));
      })
    );
  }

  getProcessInstanceRdf(processInstanceId): Observable<ICamundaProcessInstance> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/process-instance/${processInstanceId}`, {mode: 'rdf'}).pipe(
      map(result => {
        return result.report.length > 0 ? this.mapProcessInstanceFromService(result.report[0]) : JSON.parse(JSON.stringify(emptyCamundaProcessInstance));
      })
    );
  }


  getHistoryVariables(processInstanceId: string = '', taskId: string = ''): Observable<Array<ICamundaVariable>> {
    const queryParams = processInstanceId ? `?processInstanceId=${processInstanceId}` : taskId ? `?taskIdIn=${taskId}` : '';
    //return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/process-instance/${instanceId}/variables${queryParams}`, {}).pipe(
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/variable-instance${queryParams}`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapVariableFromService(item));
      })
    );
  }


  /**
   * Получить список задач через API Camunda, включая завершенные
   * @param search
   * @param searchType
   */
  getTasks(search: string = '', searchType: string = 'processInstanceId'): Observable<Array<ICamundaTask>> {
    const queryParams = `?sortBy=startTime&sortOrder=desc${search && searchType ? `&${searchType}=${search}` : ''}`;
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/task${queryParams}`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskFromService(item));
      })
    );
  }


  /**
   * Получить список задач через SQL, включая завершенные
   * @param search
   * @param searchType
   */
  getTasksRdf(search: string = '', searchType: string = 'processInstanceId'): Observable<Array<ICamundaTask>> {
    const queryParams = `?sortBy=startTime&sortOrder=desc${search && searchType ? `&${searchType}=${search}` : ''}`;
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/task${queryParams}`, {mode: 'rdf'}).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskFromService(item));
      })
    );
  }

  getTasksForProcess(processInstanceId: string, last = false): Observable<Array<ICamundaTask>> {
    const param = {
      last: last,
      mode: 'rdf'
    };
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/process-instance/${processInstanceId}/tasks`, param).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskFromService(item));
      })
    );
  }

  /**
   * Получить только активные задачи через API Camunda
   */
  getActiveTasks(): Observable<Array<ICamundaTask>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskFromService(item));
      })
    );
  }

  /**
   * Получить только активные задачи через SQL
   */
  getActiveTasksRdf(parameters: object = {}): Observable<Array<ICamundaTask>> {
    const param = {mode: 'rdf'};

    for (const paramKey in parameters) {
      param[paramKey] = parameters[paramKey]
    }
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task`, param).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskFromService(item));
      })
    );
  }

  /**
   * Получить информацию о задаче через API Camunda
   * @param taskId
   */
  getTask(taskId: string): Observable<ICamundaTask> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/task?taskId=${taskId}`, {}).pipe(
      map(result => {
        if (result.report && result.report.length) {
          return this.mapTaskFromService(result.report[0]);

        } else {
          return JSON.parse(JSON.stringify(emptyCamundaTask));
        }
      })
    );
  }

  /**
   * Получить информацию о задаче через SQL
   * @param taskId
   */
  getTaskRdf(taskId: string): Observable<ICamundaTask> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task/${taskId}`, {mode: 'rdf'}).pipe(
      map(result => {
        if (result.report && result.report.length) {
          return this.mapTaskFromService(result.report[0]);

        } else {
          return JSON.parse(JSON.stringify(emptyCamundaTask));
        }
      })
    );
  }

  getTaskDetailed(taskId: string): Observable<ICamundaTask> {
    return forkJoin([
      this.getTask(taskId)
    ]).pipe(
      map(result => {
        const rTask: ICamundaTask = result[0];
        const resultTask: ICamundaTask = {
          ...emptyCamundaTask,
          ...rTask
        };
        return resultTask;
      })
    );
  }

  getActivities(processInstanceId: string = '', taskId: string = ''): Observable<Array<ICamundaActivity>> {
    const queryParams = `?sortBy=startTime&sortOrder=asc${processInstanceId ? `&processInstanceId=${processInstanceId}` : taskId ? `&taskIdIn=${taskId}` : ''}`;
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/history/activity-instance${queryParams}`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapActivityFromService(item));
      })
    );
  }

  taskComplete(taskId: string, variables: Array<ICamundaVariable> = [], operation: 'complete' | 'resolve' = 'complete'): Observable<IServiceResponse> {
    const body = {}
    if (variables.length > 0) {
      body['variables'] = {};
      variables
        .filter(t => t.type !== 'json')
        .forEach(t => {
          body['variables'][t.name] = {
            value: t.value,
            type: t.type,
            valueInfo: t.valueInfo
          }
        });
    }
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task/${taskId}/${operation}`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }


  mapProcessDefinitionFromService(item: object): ICamundaProcessDefinition {
    const result: ICamundaProcessDefinition = {
      ...emptyCamundaProcessDefinition,
      ...item
    };
    return result;
  }

  getProcessStatusName(processInstance: ICamundaProcessInstance) {
    if (processInstance.endTime) {
      return 'Завершен';
    } else {
      return 'Выполняется';
    }
  }

  getProcessStatusColor(processInstance: ICamundaProcessInstance) {
    if (processInstance.endTime) {
      return 'green';
    } else {
      return 'blue';
    }
  }

  mapProcessInstanceFromService(item: object): ICamundaProcessInstance {
    const result: ICamundaProcessInstance = {
      ...emptyCamundaProcessInstance,
      ...item,
      processDefinition: {
        ...emptyCamundaProcessDefinition,
        id: item['processDefinitionId'] ? item['processDefinitionId'] : '',
        key: item['processDefinitionKey'] ? item['processDefinitionKey'] : '',
        name: item['processDefinitionName'] ? item['processDefinitionName'] : '',
        version: item['processDefinitionVersion'] ? item['processDefinitionVersion'] : '',
      },

      startTime: item['startTime'] ? moment(item['startTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      endTime: item['endTime'] ? moment(item['endTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      removalTime: item['removalTime'] ? moment(item['removalTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      state: item['state'],
      processProfile: {
        ...JSON.parse(JSON.stringify(emptyCamundaProcessProfile)),
        ...item['processProfile'],
        process_view_route: item['processProfile'] && item['processProfile']['process_view_route'] ? item['processProfile'] && item['processProfile']['process_view_route'] : item['processViewRoute'],
        forms: item['processProfile'] && Array.isArray(item['processProfile']['forms']) ? item['processProfile']['forms'].map(t => ({...JSON.parse(JSON.stringify(emptyCamundaForm)), ...t})) : [],
        scripts: {
          ...JSON.parse(JSON.stringify(emptyCamundaProcessProfile.scripts)),
          ...item['processProfile'] && item['processProfile']['scripts'] ? item['processProfile']['scripts'] : null
        }
      }
    };
    if (item['processVariables'] && Array.isArray(item['processVariables'])) {
      result.variables = item['processVariables'].map(t => this.mapVariableFromService(t))
    }
    return result;
  }

  mapTaskFromService(item: object): ICamundaTask {
    const processDefinition: ICamundaProcessDefinition = {
      ...JSON.parse(JSON.stringify(emptyCamundaProcessDefinition)),
      id: item['processDefinitionId'] ? item['processDefinitionId'] : '',
      key: item['processDefinitionKey'] ? item['processDefinitionKey'] : '',
      name: item['processDefinitionName'] ? item['processDefinitionName'] : '',
      version: item['processDefinitionVersion'] ? item['processDefinitionVersion'] : ''
    };

    const processInstance: ICamundaProcessInstance = {
      ...JSON.parse(JSON.stringify(emptyCamundaProcessInstance)),
      id: item['processInstanceId'] ? item['processInstanceId'] : '',
      processDefinitionId: item['processDefinitionId'] ? item['processDefinitionId'] : '',
      businessKey: item['processBusinessKey'] ? item['processBusinessKey'] : '',
      tenantId: '',
      startTime: item['startTime'] ? moment(item['startTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      endTime: item['endTime'] ? moment(item['endTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,

      durationInMillis: item['duration'] ? item['duration'] : 0,
      processDefinition: processDefinition
    }

    const result: ICamundaTask = {
      ...JSON.parse(JSON.stringify(emptyCamundaTask)),
      ...item,
      processDefinition: processDefinition,
      processInstance: processInstance,
      created: item['created'] ? moment(item['created'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      due: item['due'] ? moment(item['due'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      startTime: item['startTime'] ? moment(item['startTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      endTime: item['endTime'] ? moment(item['endTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      // taskViewRoute: item['taskViewRoute'] && Array.isArray(item['taskViewRoute'])?item['taskViewRoute']:[],
      formValidator: {...JSON.parse(JSON.stringify(emptyCamundaFormValidator)), ...item['taskFormValidator']},
      taskProfile: {
        ...JSON.parse(JSON.stringify(emptyCamundaTaskProfile)),
        ...item['taskProfile']
      }
      // taskProfile:{
      //   ...JSON.parse(JSON.stringify(emptyCamundaTaskProfile)),
      //   ...item['taskProfile'],
      //   complete_buttons: item['taskProfile'] && Array.isArray(item['taskProfile']['complete_buttons'])?item['taskProfile']['complete_buttons'].map(
      //     t=>({...JSON.parse(JSON.stringify(emptyCamundaCompleteButton), ...t)})
      //   ):[],
      //   action_buttons: item['taskProfile'] && Array.isArray(item['taskProfile']['action_buttons'])?item['taskProfile']['action_buttons'].map(
      //     t=>({type: 'unknown', ...t})
      //   ):[],
      //   forms: item['taskProfile'] && Array.isArray(item['taskProfile']['forms'])?item['taskProfile']['forms'].map(
      //     t=>({...JSON.parse(JSON.stringify(emptyCamundaForm), ...t)})
      //   ):[],
      //   task_view_route: item['taskProfile'] && Array.isArray(item['taskProfile']['task_view_route'])?item['taskProfile']['task_view_route']:[]
      // }
    };
    if (!item['created']) {
      result.created = result.startTime;
    }
    if (item['processVariables'] && Array.isArray(item['processVariables'])) {
      result.processInstance.variables = item['processVariables'].map(t => this.mapVariableFromService(t))
    }
    // проставляем дефолтные значения для кнопок
    result.taskProfile.complete_buttons.forEach(t => {
      for (const key in emptyCamundaCompleteButton) {
        if (!t[key]) {
          t[key] = emptyCamundaCompleteButton[key]
        }
      }
    });
    // if (item['taskForms'] && Array.isArray(item['taskForms'])) {
    //   const tmp = item['taskForms'].sort(function (a, b) {
    //     if (a.order > b.order) {
    //       return 1;
    //     }
    //     if (a.order < b.order) {
    //       return -1;
    //     }
    //     // a должно быть равным b
    //     return 0;
    //   })
    //
    //
    //
    //   result.forms = tmp.map(t=>({
    //     ...JSON.parse(JSON.stringify(emptyCamundaForm)),
    //     id: '',
    //     formCode: t['form_code'],
    //     title: t['title']?t['title']:'',
    //     showTitle: t['show_title']?t['show_title']:false,
    //     tooltip: t['tooltip']?t['tooltip']:'',
    //     translatedTooltip: {...emptyTranslatedName},
    //     image: t['image']?t['image']:'',
    //     showImage: true,
    //     userLogins: t['user_logins']?t['user_logins']:[],
    //     userGroupIds:  t['user_group_ids']?t['user_group_ids']:[],
    //     profile: t['profile']?t['profile']:{},
    //   }))
    // }
    return result;
  }

  mapVariableFromService(item: object): ICamundaVariable {
    const result: ICamundaVariable = {
      ...JSON.parse(JSON.stringify(emptyCamundaVariable)),
      id: item['id'] ? item['id'] : '',
      name: item['name'],
      type: item['type'],
      value: item['type'] === 'json' ? JSON.parse(item['value']) : item['value'],
      valueInfo: item['valueInfo'],
    };
    return result;
  }

  mapVariableToService(item: ICamundaVariable) {
    const result = {
      name: item.name,
      value: item.value,
      type: item.type,
      valueInfo: item.valueInfo

    }
  }

  mapActivityFromService(item: object): ICamundaActivity {
    const result: ICamundaActivity = {
      ...emptyCamundaActivity,
      ...item,

      startTime: item['startTime'] ? moment(item['startTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,
      endTime: item['endTime'] ? moment(item['endTime'], 'YYYY-MM-DD HH:mm:SS').toDate() : null,

      processDefinition: {
        ...emptyCamundaProcessDefinition,
        id: item['processDefinitionId'] ? item['processDefinitionId'] : '',
        key: item['processDefinitionKey'] ? item['processDefinitionKey'] : '',
      },
    };

    return result;
  }

  /**
   * Обрезать список типов процессов - оставить только с максимальной версией
   * @param list
   */
  getLatestProcessDefinitionList(list: Array<ICamundaProcessDefinition>): Array<ICamundaProcessDefinition> {
    const result: Array<ICamundaProcessDefinition> = []
    list.forEach(t => {
      const idx = result.findIndex(k => k.key === t.key);
      if (idx > 0) {
        if (result[idx].version < t.version)
          result[idx] = t;
      } else {
        result.push(t);
      }
    })
    return result;
  }

  /**
   * Получить из таска значение переменной процесса
   * @param task
   * @param variableName
   */
  getProcessVariableValueFromTask(task: ICamundaTask, variableName: string): string | number | object {
    const variable: ICamundaVariable = task.processInstance.variables.find(t => t.name === variableName);
    return variable ? variable.value : '';
  }

  /**
   * Получить из инстанса процесса значение переменной процесса
   * @param processInstance
   * @param variableName
   */
  getProcessVariableValueFromProcessInstance(processInstance: ICamundaProcessInstance, variableName: string): string | number | object {
    const variable: ICamundaVariable = processInstance.variables.find(t => t.name === variableName);
    return variable ? variable.value : '';
  }

  setVariableToTask(task: ICamundaTask, variableName, value: string | number | object) {
    if (task && task.processInstance && task.processInstance.variables) {
      let variable = task.processInstance.variables.find(k => k.name === variableName);
      if (variable) {
        variable.value = value;
      } else {
        variable = JSON.parse(JSON.stringify(emptyCamundaVariable));
        variable.name = variableName;
        variable.value = value;
        switch (typeof value) {
          case "number":
            variable.type = 'Number';
            break;
          case "string":
            variable.type = 'String';
            break;
          case "object":
            variable.type = 'json';
            break
          default:
            variable.type = 'String';
        }
        task.processInstance.variables.push(variable);
      }


    }
  }


  getTaskLastState(task: ICamundaTask): Observable<ICamundaTaskState<any>> {
    const apiUrl = task.processProfile.state_api_url;

//    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.id}/tasks/${task.id}/state`, {condition: 'last'}).pipe(
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.businessKey}/tasks/${task.id}/state`, {condition: 'last'}).pipe(
      map(result => {
        if (result.report && result.report[0]) {
          const item = result.report[0];
          return this.mapTaskStateFromService(item);
        } else {
          return JSON.parse(JSON.stringify(emptyCamundaTaskState))
        }
      })
    );
  }

  getProcessLastState(task: ICamundaTask): Observable<ICamundaTaskState<any>> {
    const apiUrl = task.processProfile.state_api_url;

//    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.id}/state`, {condition: 'last'}).pipe(
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.businessKey}/state`, {condition: 'last'}).pipe(
      map(result => {
        if (result.report && result.report[0]) {
          const item = result.report[0];
          return this.mapTaskStateFromService(item);
        } else {
          return JSON.parse(JSON.stringify(emptyCamundaTaskState))
        }
      })
    );
  }

  getProcessStates(task: ICamundaTask): Observable<Array<ICamundaTaskState<any>>> {
    const apiUrl = task.processProfile.state_api_url;

    // return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.id}/state`, {}).pipe(
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(apiUrl)}/camunda/processes/${task.processInstance.businessKey}/state`, {}).pipe(
      map(result => {
        return result.report.map(item => this.mapTaskStateFromService(item));
      })
    );
  }

  createProcessState(serviceApiUrl: string, processDefinitionKey: string, processInstanceId: string, taskId: string, objectId: string, state: ICamundaTaskState<any>): Observable<IServiceResponse> {
    const body = {
      process_definition_key: processDefinitionKey,
      comment: state.comment,
      object_id: objectId,
      state: state.state,
      profile: state.profile
    }

    return this.dataService.postData(`${this.configurationManager.getApiUrl(serviceApiUrl)}/camunda/processes/${processInstanceId}/tasks/${taskId ? taskId : 'init'}/state`, {}, body);
  }

  updateTaskState(serviceApiUrl: string, processDefinitionKey: string, processInstanceId: string, taskId: string, objectId: string, state: ICamundaTaskState<any>): Observable<IServiceResponse> {
    const body = {
      process_definition_key: processDefinitionKey,
      comment: state.comment,
      object_id: objectId,
      state: state.state,
      profile: state.profile
    }

    return state.id ?
      this.dataService.putData(`${this.configurationManager.getApiUrl(serviceApiUrl)}/camunda/processes/${processInstanceId}/tasks/${taskId}/state/${state.id}`, {}, body) :
      this.dataService.postData(`${this.configurationManager.getApiUrl(serviceApiUrl)}/camunda/processes/${processInstanceId}/tasks/${taskId}/state`, {}, body);


  }

  mapTaskStateFromService(item: Object): ICamundaTaskState<any> {
    const result: ICamundaTaskState<any> = {
      ...JSON.parse(JSON.stringify(emptyCamundaTaskState)),
      id: item['id'].toString(),
      partyId: item['party_id'].toString(),
      dateCreate: moment(item['create_date'], 'YYYYMMDD HH:mm:ss').toDate(),

      processDefinitionKey: item['process_definition_key'],
      processInstanceId: item['process_instance_id'],
      taskId: item['task_id'],

      userId: item['user_id'],
      userName: item['user_name'],
      userFirstName: item['user_first_name'],
      userLastName: item['user_last_name'],
      position: item['position'],
      comment: item['comment'],
      objectId: item['object_id'],
      profile: {
        ...JSON.parse(JSON.stringify(emptyCamundaTaskProfile)),
        ...item['profile']
      },
      state: item['state']
    };
    return result;
  }

  /** Назначить задачу на пользователя
   *
   * @param taskId
   * @param login
   */
  assignTask(taskId: string, login: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task/${taskId}/assignee`, {}, {userId: login}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  /**
   * Claim a task for a specific user.
   * Note: The difference with set a assignee is that here a check is performed to see if the task already has a user assigned to it.
   * @param taskId
   * @param login
   */
  claimTask(taskId: string, login: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task/${taskId}/claim`, {}, {userId: login}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  /**
   * Resets a task’s assignee. If successful, the task is not assigned to a user.
   * @param taskId
   */
  unclaimTask(taskId: string): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiWorkflow2Url')}/camunda/task/${taskId}/unclaim`, {}, {}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

}
